<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="파트너스 톡"
        subtitle="파트너들과 실시간 대화"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-4 h-[calc(100vh-180px)]">
          <!-- 좌측: 채팅방 목록 -->
          <div class="lg:col-span-1 bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
            <div class="px-4 py-3 border-b border-slate-100">
              <p class="text-[14px] font-bold text-slate-900">채팅방</p>
            </div>

            <div class="flex-1 overflow-y-auto p-2 space-y-1">
              <button
                v-for="room in chatRooms"
                :key="room.id"
                @click="selectRoom(room.id)"
                class="w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left"
                :class="currentRoom === room.id
                  ? 'bg-indigo-50 border border-indigo-200'
                  : 'hover:bg-slate-50'"
              >
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  :class="room.bgColor"
                >
                  <component :is="room.icon" class="w-5 h-5" :class="room.iconColor" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-[13px] font-bold text-slate-900 truncate">{{ room.name }}</p>
                  <p class="text-[11px] text-slate-400 truncate">{{ room.description }}</p>
                </div>
                <!-- 새 메시지 표시 -->
                <div
                  v-if="room.unread > 0"
                  class="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                >
                  {{ room.unread > 9 ? '9+' : room.unread }}
                </div>
              </button>
            </div>
          </div>

          <!-- 우측: 채팅 영역 -->
          <div class="lg:col-span-3 bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
            <!-- 상단 정보바 -->
            <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between" :class="currentRoomData?.headerBg || 'bg-slate-50'">
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center"
                  :class="currentRoomData?.bgColor || 'bg-slate-200'"
                >
                  <component
                    :is="currentRoomData?.icon"
                    class="w-5 h-5"
                    :class="currentRoomData?.iconColor || 'text-slate-500'"
                  />
                </div>
                <div>
                  <p class="text-[14px] font-bold text-slate-900">{{ currentRoomData?.name || '채팅방' }}</p>
                  <p class="text-[12px] text-slate-400">{{ currentRoomData?.description || '' }}</p>
                </div>
              </div>
              <div class="text-[12px] text-slate-500">
                파트너 전용
              </div>
            </div>

            <!-- 메시지 목록 -->
            <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-4">
              <div v-if="loadingMessages" class="flex items-center justify-center py-20">
                <div class="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>

              <template v-else>
                <!-- 환영 메시지 -->
                <div class="flex justify-center mb-4">
                  <div class="px-4 py-2 rounded-2xl" :class="currentRoomData?.welcomeBg || 'bg-indigo-50'">
                    <p class="text-[13px] font-medium text-center" :class="currentRoomData?.welcomeText || 'text-indigo-600'">
                      {{ currentRoomData?.welcomeMessage || '파트너스 여러분, 함께 채팅을 즐겨보세요!' }}
                    </p>
                  </div>
                </div>

                <!-- 날짜 구분선 -->
                <div v-if="messages.length > 0" class="flex items-center justify-center">
                  <span class="px-3 py-1 bg-slate-100 text-slate-400 text-[11px] font-medium rounded-full">
                    오늘
                  </span>
                </div>

                <div
                  v-for="message in messages"
                  :key="message.id"
                  class="flex gap-3"
                  :class="message.authorId === user?.uid ? 'flex-row-reverse' : ''"
                >
                  <!-- 아바타 -->
                  <div
                    v-if="message.authorId !== user?.uid"
                    class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-[14px] font-bold"
                    :style="{ backgroundColor: getAvatarColor(message.authorId), color: 'white' }"
                  >
                    {{ getInitial(message.authorName || message.authorEmail) }}
                  </div>

                  <div :class="message.authorId === user?.uid ? 'items-end' : 'items-start'" class="flex flex-col max-w-[70%]">
                    <!-- 닉네임 -->
                    <span v-if="message.authorId !== user?.uid" class="text-[12px] font-medium text-slate-500 mb-1 ml-1">
                      {{ message.authorName || maskEmail(message.authorEmail) }}
                    </span>
                    <!-- 메시지 버블 -->
                    <div
                      class="px-4 py-2.5 rounded-2xl text-[14px] leading-relaxed"
                      :class="message.authorId === user?.uid
                        ? `${currentRoomData?.myBubble || 'bg-indigo-600'} text-white rounded-br-md`
                        : 'bg-slate-100 text-slate-900 rounded-bl-md'"
                    >
                      {{ message.content }}
                    </div>
                    <span class="text-[11px] text-slate-300 mt-1 mx-1">
                      {{ formatTime(message.createdAt) }}
                    </span>
                  </div>
                </div>

                <div v-if="messages.length === 0" class="text-center py-12">
                  <div class="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                  </div>
                  <p class="text-[15px] font-medium text-slate-500">아직 메시지가 없습니다</p>
                  <p class="text-[13px] text-slate-400 mt-1">첫 인사를 남겨보세요!</p>
                </div>
              </template>
            </div>

            <!-- 입력창 -->
            <form @submit.prevent="sendMessage" class="p-4 border-t border-slate-100 bg-white">
              <div class="flex gap-3">
                <input
                  v-model="newMessage"
                  type="text"
                  :placeholder="currentRoomData?.placeholder || '메시지를 입력하세요...'"
                  class="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-[14px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                  :disabled="sending"
                  @keydown.enter.prevent="sendMessage"
                />
                <button
                  type="submit"
                  :disabled="!newMessage.trim() || sending"
                  class="px-5 py-3 text-white text-[14px] font-bold rounded-xl transition-all disabled:opacity-50 flex items-center gap-2"
                  :class="currentRoomData?.sendBtn || 'bg-indigo-600 hover:bg-indigo-700'"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                  </svg>
                  <span class="hidden sm:inline">전송</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick, watch, h } from 'vue'
import { collection, query, orderBy, limit, addDoc, onSnapshot, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

// 아이콘 컴포넌트들
const ChatIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' })
    ])
  }
}

const TrophyIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' })
    ])
  }
}

const QuestionIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' })
    ])
  }
}

const InfoIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' })
    ])
  }
}

export default {
  name: 'PartnersChatView',
  components: {
    PartnersLayout,
    PartnersHeader,
    ChatIcon,
    TrophyIcon,
    QuestionIcon,
    InfoIcon
  },
  setup() {
    const { user, userProfile } = useAuth()

    const currentRoom = ref('free')
    const messages = ref([])
    const newMessage = ref('')
    const loadingMessages = ref(true)
    const sending = ref(false)
    const messagesContainer = ref(null)

    let unsubscribe = null

    // 채팅방 목록
    const chatRooms = ref([
      {
        id: 'free',
        name: '자유방',
        description: '자유롭게 대화해요',
        icon: ChatIcon,
        bgColor: 'bg-indigo-100',
        iconColor: 'text-indigo-600',
        headerBg: 'bg-indigo-50',
        welcomeBg: 'bg-indigo-50',
        welcomeText: 'text-indigo-600',
        welcomeMessage: '자유롭게 대화를 나눠보세요!',
        myBubble: 'bg-indigo-600',
        sendBtn: 'bg-indigo-600 hover:bg-indigo-700',
        placeholder: '자유롭게 이야기해보세요...',
        unread: 0
      },
      {
        id: 'contract',
        name: '계약자랑방',
        description: '계약 성공을 축하해요',
        icon: TrophyIcon,
        bgColor: 'bg-amber-100',
        iconColor: 'text-amber-600',
        headerBg: 'bg-amber-50',
        welcomeBg: 'bg-amber-50',
        welcomeText: 'text-amber-600',
        welcomeMessage: '계약 성공을 함께 축하해요!',
        myBubble: 'bg-amber-500',
        sendBtn: 'bg-amber-500 hover:bg-amber-600',
        placeholder: '계약 성공 소식을 공유해주세요...',
        unread: 0
      },
      {
        id: 'question',
        name: '질문방',
        description: '궁금한 점을 물어보세요',
        icon: QuestionIcon,
        bgColor: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        headerBg: 'bg-emerald-50',
        welcomeBg: 'bg-emerald-50',
        welcomeText: 'text-emerald-600',
        welcomeMessage: '궁금한 점을 편하게 질문하세요!',
        myBubble: 'bg-emerald-600',
        sendBtn: 'bg-emerald-600 hover:bg-emerald-700',
        placeholder: '질문을 입력하세요...',
        unread: 0
      },
      {
        id: 'info',
        name: '정보공유방',
        description: '유용한 정보를 나눠요',
        icon: InfoIcon,
        bgColor: 'bg-violet-100',
        iconColor: 'text-violet-600',
        headerBg: 'bg-violet-50',
        welcomeBg: 'bg-violet-50',
        welcomeText: 'text-violet-600',
        welcomeMessage: '유용한 정보와 팁을 공유해주세요!',
        myBubble: 'bg-violet-600',
        sendBtn: 'bg-violet-600 hover:bg-violet-700',
        placeholder: '정보나 팁을 공유해주세요...',
        unread: 0
      }
    ])

    // 현재 채팅방 데이터
    const currentRoomData = computed(() => {
      return chatRooms.value.find(r => r.id === currentRoom.value)
    })

    // 채팅방 변경
    const selectRoom = (roomId) => {
      if (currentRoom.value === roomId) return
      currentRoom.value = roomId
      // unread 초기화
      const room = chatRooms.value.find(r => r.id === roomId)
      if (room) room.unread = 0
    }

    // 이메일 마스킹
    const maskEmail = (email) => {
      if (!email) return '익명'
      const [localPart, domain] = email.split('@')
      if (!localPart || !domain) return '익명'
      if (localPart.length <= 2) {
        return `${localPart}**@${domain}`
      }
      return `${localPart.slice(0, 2)}**@${domain}`
    }

    // 메시지 실시간 구독
    const subscribeMessages = () => {
      if (unsubscribe) unsubscribe()

      loadingMessages.value = true
      const collectionName = `partnerChat_${currentRoom.value}`

      const q = query(
        collection(db, collectionName),
        orderBy('createdAt', 'desc'),
        limit(100)
      )

      unsubscribe = onSnapshot(q, (snapshot) => {
        const newMessages = []
        snapshot.forEach((doc) => {
          newMessages.push({
            id: doc.id,
            ...doc.data()
          })
        })
        messages.value = newMessages.reverse()
        loadingMessages.value = false

        nextTick(() => scrollToBottom())
      }, (error) => {
        console.error('메시지 구독 에러:', error)
        loadingMessages.value = false
      })
    }

    // 메시지 전송
    const sendMessage = async () => {
      if (!newMessage.value.trim() || sending.value || !user.value) return

      const content = newMessage.value.trim()
      newMessage.value = ''
      sending.value = true

      try {
        const collectionName = `partnerChat_${currentRoom.value}`
        await addDoc(collection(db, collectionName), {
          content,
          authorId: user.value.uid,
          authorEmail: userProfile.value?.email || user.value.email || '익명',
          authorName: userProfile.value?.name || null,
          createdAt: serverTimestamp()
        })
      } catch (error) {
        console.error('메시지 전송 실패:', error)
        newMessage.value = content
      } finally {
        sending.value = false
      }
    }

    // 스크롤 맨 아래로
    const scrollToBottom = () => {
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    }

    // 시간 포맷
    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    }

    // 이니셜
    const getInitial = (nameOrEmail) => {
      if (!nameOrEmail) return '?'
      return nameOrEmail.charAt(0).toUpperCase()
    }

    // 아바타 색상
    const getAvatarColor = (userId) => {
      const colors = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6']
      if (!userId) return colors[0]
      const index = userId.charCodeAt(0) % colors.length
      return colors[index]
    }

    // 채팅방 변경 시 구독 갱신
    watch(currentRoom, () => {
      subscribeMessages()
    })

    onMounted(() => {
      subscribeMessages()
    })

    onUnmounted(() => {
      if (unsubscribe) unsubscribe()
    })

    return {
      user,
      currentRoom,
      chatRooms,
      currentRoomData,
      messages,
      newMessage,
      loadingMessages,
      sending,
      messagesContainer,
      selectRoom,
      maskEmail,
      sendMessage,
      formatTime,
      getInitial,
      getAvatarColor
    }
  }
}
</script>
